<template>
  <a-modal :title="modalTitle" :visible="visible" @ok="handleOk" @cancel="$emit('update:visible', false)" @afterClose="closeModal" okText="确认" cancelText="取消" width="500px">
    <a-form ref="formRef" :model="formState" :label-col="{ span: 6 }" :wrapper-col="{ span: 16 }">

      <a-form-item label="类型名称" name="value" :rules="[{ required: true, message: '请输入商品类型名称' }]">
        <a-input v-model:value="formState.value" placeholder="请输入商品类型名称" />
      </a-form-item>

      <a-form-item label="类型图片" name="imageUuid">
        <a-upload v-model:file-list="fileList" name="file" list-type="picture-card" :show-upload-list="false" :custom-request="uploadImage" :before-upload="beforeUpload" class="upload-wrapper">

          <div v-if="formState.imageUuid" class="upload-preview">
            <img :src="imageUrl + formState.imageUuid" alt="类型图片" />
            <div class="upload-actions" @click.stop="removeImage">
              <DeleteOutlined />
            </div>
          </div>

          <div v-else>
            <loading-outlined v-if="loading"></loading-outlined>
            <plus-outlined v-else></plus-outlined>
            <div class="ant-upload-text">上传图片</div>
          </div>
        </a-upload>
      </a-form-item>

      <a-form-item v-if="isEdit" label="ID" name="id" style="display: none;">
        <a-input v-model:value="formState.id" disabled />
      </a-form-item>

    </a-form>
  </a-modal>
</template>

<script>
import { PlusOutlined, LoadingOutlined, DeleteOutlined } from "@ant-design/icons-vue";
import { AddCategory, UpdateCategory } from "/src/api/shopping.js";
import { Upload } from "/src/api/upload.js";
import { message } from "ant-design-vue";
import { cloneDeep } from "lodash";

export default {
  name: "商品类型新增编辑弹窗",
  components: {
    PlusOutlined,
    LoadingOutlined,
    DeleteOutlined,
  },
  props: {
    visible: { type: Boolean, default: false },
    record: { type: Object, default: () => ({}) }
  },
  emits: ['update:visible', 'ok'],
  data() {
    return {
      loading: false,
      imageUrl: '/api/file/img/',
      fileList: [],
      formState: { // 表单数据模型
        id: undefined,
        value: undefined,
        imageUuid: undefined,
        name: '商城产品类别',
        category: '商城设置',
      },
      formRef: null,
    };
  },
  computed: {
    isEdit() {
      return !!this.formState.id;
    },
    modalTitle() {
      return this.isEdit ? "编辑商品类型" : "新增商品类型";
    }
  },
  watch: {
    record: {
      handler(newRecord) {
        if (newRecord && newRecord.id) {
          this.formState = { ...this.formState, ...cloneDeep(newRecord) };
        }
      },
      immediate: true,
      deep: true,
    }
  },
  methods: {
    /**
     * 核心重置方法：用于在成功提交或取消后清理数据
     */
    resetFormState() {
      // 1. 重置表单数据模型
      this.formState = {
        id: undefined,
        value: undefined,
        imageUuid: undefined,
        name: '商品类型',
        category: '商城设置',
      };
      // 2. 重置其他状态
      this.fileList = [];
      this.loading = false;
    },

    removeImage() {
      this.formState.imageUuid = undefined;
      this.fileList = [];
      message.success('图片已移除');
    },

    beforeUpload(file) {
      const isJpgOrPng = file.type === 'image/jpeg' || file.type === 'image/png';
      if (!isJpgOrPng) {
        message.error('只能上传 JPG/PNG 文件!');
      }
      return isJpgOrPng;
    },

    async uploadImage({ file, onSuccess, onError }) {
      this.loading = true;
      let formData = new FormData();
      formData.append('file', file);
      formData.append('category', 'portrait');
      formData.append('system', 'true');

      try {
        const res = await Upload(formData);

        if (res.errCode === 0 && res.data) {
          this.formState.imageUuid = res.data;
          message.success('图片上传成功');
          onSuccess(res);
        } else {
          message.error('上传失败：' + (res.message || '未知错误'));
          onError(new Error('Upload failed'));
        }
      } catch (error) {
        console.error("uploadImage error:", error);
        message.error('网络错误，上传失败');
        onError(error);
      } finally {
        this.loading = false;
      }
    },

    // 模态框确认按钮的回调
    async handleOk() {
      try {
        // 1. 执行表单校验
        // 如果校验失败，Promise 会被拒绝 (reject)，代码会跳转到 catch 块
        await this.$refs.formRef.validate();

        let res;
        // 2. 调用 API (校验通过才会执行到这里)
        if (this.isEdit) {
          res = await UpdateCategory(this.formState);
        } else {
          const { id, ...addData } = this.formState;
          res = await AddCategory(addData);
        }

        // 3. 处理 API 业务响应
        if (res.errCode === 0) {
          message.success("操作成功");
          this.resetFormState();
          this.$emit('ok');
          this.$emit('update:visible', false);
        } else {
          message.error(res.errMsg || "操作失败");
        }
      } catch (error) {
        // 捕获 API 请求异常 或 表单校验失败

        // 1. 检查是否是 Ant Design 的表单校验错误 (error 对象包含 errorFields 属性)
        if (error && error.errorFields && error.errorFields.length > 0) {
          // 提取第一个错误信息
          const firstErrorMsg = error.errorFields[0].errors[0];

          // 全局警告提示用户具体未填的项
          message.warning(`请检查表单：${firstErrorMsg}`);
          console.error('Validation failed:', error);

        } else if (error !== false) {
          // 2. 捕获 API 请求异常 (非校验错误)
          console.error('API Error:', error);
          message.error('请求异常，请稍后重试');
        }
        // 如果是校验失败 (且 error.errorFields 为空)，则依赖 AntD 原生红色提示，无需重复 message.error。
      }
    },

    // 模态框 `@afterClose` 的回调：重置表单数据和校验状态
    closeModal() {
      // 确保在任何情况下关闭（取消、ESC、点击遮罩）都能重置数据
      this.resetFormState();
      // 重置表单校验状态
      if (this.$refs.formRef) {
        this.$refs.formRef.resetFields();
      }
    }
  },
};
</script>

<style scoped>
/* 统一上传区域的尺寸，确保预览框和上传按钮大小一致 */
.upload-wrapper {
  width: 104px;
  height: 104px;
}

/* 预览容器样式 - 启用绝对定位 */
.upload-preview {
  position: relative;
  width: 100%;
  height: 100%;
  overflow: hidden;
}

/* 预览图片样式 */
.upload-preview img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

/* ========== 悬浮操作层样式 (删除按钮) ========== */
.upload-actions {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5); /* 半透明黑色背景 */
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  font-size: 20px;
  cursor: pointer;
  opacity: 0; /* 默认隐藏 */
  transition: opacity 0.3s;
  z-index: 5;
}

/* 悬停时显示操作层 */
.upload-preview:hover .upload-actions {
  opacity: 1;
}
</style>